import { adminSocket } from "@shared/socket-namespaces/admin/admin.socket";
import { useAdminEvent } from "@shared/frontend/hooks/use-admin-event";
import { linko } from "@shared/linko";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import useQuestion from "../dialog-components/QuestionDialog";
import {
  patchLeadHandlingInList,
  refreshLeadHandlingBadges,
} from "../queries/invalidate-lead-handling";
import { invalidateOpportunityHandlingQueries } from "../queries/invalidate-opportunity-handling";
import { useOrganization } from "./OrganizationProvider";
import { useUser } from "./UserProvider";

interface AdminHandlingContextValue {
  connected: boolean;
}

const AdminHandlingContext = createContext<AdminHandlingContextValue>({
  connected: false,
});

export const useAdminHandling = () => useContext(AdminHandlingContext);

export const AdminHandlingProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentUser } = useUser();
  const { currOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const question = useQuestion();
  const [connected, setConnected] = useState(adminSocket.connected);
  const promptingRef = useRef<Set<string>>(new Set());

  const invalidateHandling = useCallback(
    (opportunityId: string) => {
      void invalidateOpportunityHandlingQueries(queryClient, opportunityId);
    },
    [queryClient],
  );

  useAdminEvent("opportunity:handling-changed", (data) => {
    invalidateHandling(data.opportunityId);
  });

  useAdminEvent("lead:handling-changed", (data) => {
    patchLeadHandlingInList(queryClient, data);
    // Patch is enough for the list - only refresh lightweight badges.
    refreshLeadHandlingBadges(queryClient);
  });

  useAdminEvent("opportunity:handling-expiring", (data) => {
    if (currentUser?.isCallCenterStation) {
      const workbench = queryClient.getQueryData<{
        currentTask?: { opportunity: { id: string } } | null;
      }>(["call-center-workbench", currentUser.id]);
      if (workbench?.currentTask?.opportunity.id !== data.opportunityId) return;
    }
    if (promptingRef.current.has(data.opportunityId)) return;
    promptingRef.current.add(data.opportunityId);

    void (async () => {
      const stillWorking = await question(
        `עברה שעה מאז שסימנת "${data.title}" בטיפול שלך. עדיין עובד/ת עליה?`,
        "",
        { confirmText: "כן, עדיין בטיפול", cancelText: "שחרר טיפול" },
      );

      try {
        if (stillWorking === true) {
          await linko.opportunity.mutations.extendHandling({
            opportunityId: data.opportunityId,
          });
          invalidateHandling(data.opportunityId);
        } else if (stillWorking === false) {
          await linko.opportunity.mutations.releaseHandling({
            opportunityId: data.opportunityId,
          });
          invalidateHandling(data.opportunityId);
        }
        // null (סגירת דיאלוג) — לא עושים כלום; grace period יטפל
      } finally {
        promptingRef.current.delete(data.opportunityId);
      }
    })();
  });

  useAdminEvent("lead:handling-expiring", (data) => {
    if (currentUser?.isCallCenterStation) return;
    if (promptingRef.current.has(data.leadId)) return;
    promptingRef.current.add(data.leadId);

    void (async () => {
      const stillWorking = await question(
        `עברה שעה מאז שסימנת את הליד ${data.phoneNumber} בטיפול שלך. עדיין עובד/ת עליו?`,
        "",
        { confirmText: "כן, עדיין בטיפול", cancelText: "שחרר טיפול" },
      );

      try {
        if (stillWorking === true) {
          const res =
            await linko.summerCampaignLead.mutations.extendLeadHandling({
              leadId: data.leadId,
            });
          patchLeadHandlingInList(queryClient, {
            leadId: data.leadId,
            organizationId: currOrganization?.id ?? "",
            handlingManagerId: res.data.handlingManagerId,
            handlingManagerName: res.data.handlingManagerName ?? null,
            handlingSince: res.data.handlingSince ?? null,
          });
          refreshLeadHandlingBadges(queryClient);
        } else if (stillWorking === false) {
          await linko.summerCampaignLead.mutations.releaseLeadHandling({
            leadId: data.leadId,
          });
          patchLeadHandlingInList(queryClient, {
            leadId: data.leadId,
            organizationId: currOrganization?.id ?? "",
            handlingManagerId: null,
            handlingManagerName: null,
            handlingSince: null,
          });
          refreshLeadHandlingBadges(queryClient);
        }
      } finally {
        promptingRef.current.delete(data.leadId);
      }
    })();
  });

  useEffect(() => {
    if (!currentUser?.id) {
      adminSocket.disconnect();
      setConnected(false);
      return;
    }

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    adminSocket.on("connect", onConnect);
    adminSocket.on("disconnect", onDisconnect);

    if (!adminSocket.connected) {
      adminSocket.connect();
    } else {
      setConnected(true);
    }

    return () => {
      adminSocket.off("connect", onConnect);
      adminSocket.off("disconnect", onDisconnect);
      adminSocket.disconnect();
      setConnected(false);
    };
  }, [currentUser?.id]);

  useEffect(() => {
    if (!connected || !currOrganization?.id) return;
    adminSocket.emit("org:join", { organizationId: currOrganization.id });
  }, [connected, currOrganization?.id]);

  return (
    <AdminHandlingContext.Provider value={{ connected }}>
      {children}
    </AdminHandlingContext.Provider>
  );
};
